/**
 * Robust Day Tracking System
 * Source of truth: Firestore programSelection
 *
 * PHILOSOPHY (based on industry best practices):
 * - Workouts persist until completed - they don't disappear based on calendar
 * - Users can go back to any previous day at any time
 * - "Current day" = next uncompleted workout in sequence
 * - No automatic skipping - user must explicitly skip or complete
 * - Gentle nudges for being "behind schedule" but no forced progression
 */

import { getUserProgramSelection, saveUserProgramSelection } from './programManagement';
import type { UserProgramSelection } from './types/program';
import { db as dexieDb } from './db';

/**
 * Normalize a date to midnight UTC for consistent day calculations
 */
function normalizeToMidnightUTC(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}

/**
 * The program's real length in weeks, from its own metadata.
 *
 * Program durations are data-driven (12, 13, 14, 37, and several short
 * programs), but four separate places in this file hardcoded 13 — clamping
 * long-program users backward into week 13 and rejecting navigation past it.
 * Every one of them now goes through here.
 *
 * Falls back to 13 only when the program genuinely cannot be resolved (offline
 * with a cold cache); callers that validate user input should treat a fallback
 * as "unknown" rather than a hard limit.
 */
async function resolveProgramDurationWeeks(
  programId: string,
  userId: string
): Promise<number> {
  try {
    const { getCachedProgram } = await import('./programCache');
    const program = await getCachedProgram(programId, userId);
    const weeks = program?.metadata?.duration;
    if (typeof weeks === 'number' && weeks > 0) return weeks;
  } catch {
    // Cache miss / offline — fall through.
  }
  return 13;
}

/**
 * Calculate how many days behind schedule the user is (informational only)
 * This does NOT auto-skip - just provides info for UI nudges
 */
export function calculateScheduleStatus(
  startDate: Date,
  currentWeek: number,
  currentDay: number,
  completedDays: { week: number; day: number }[],
  /**
   * The program's real length. Omit only when it genuinely isn't known.
   *
   * This was hardcoded to 13, which clamped expectedWeek but NOT expectedDay
   * or daysBehind — so a user on the 37-week prenatal program sitting at week
   * 20 was told the calendar expected them at week 13, and auto-advance
   * dragged them backward and pinned them there permanently.
   */
  maxWeeks?: number
): {
  daysBehind: number;
  expectedWeek: number;
  expectedDay: number;
  isOnTrack: boolean;
  message: string;
} {
  const startNormalized = normalizeToMidnightUTC(startDate);
  const nowNormalized = normalizeToMidnightUTC(new Date());
  const daysSinceStart = Math.floor((nowNormalized.getTime() - startNormalized.getTime()) / (1000 * 60 * 60 * 24));

  // Future start date — program hasn't begun yet
  if (daysSinceStart < 0) {
    const daysUntilStart = Math.abs(daysSinceStart);
    return {
      daysBehind: 0,
      expectedWeek: 1,
      expectedDay: 1,
      isOnTrack: true,
      message: `Program starts in ${daysUntilStart} day${daysUntilStart !== 1 ? 's' : ''}`,
    };
  }

  // Where user WOULD be if they did 1 workout per calendar day.
  // Clamp the TOTAL first, then derive week and day from the clamped value, so
  // the two can never disagree. Clamping the week alone (the old behaviour) let
  // expectedDay keep cycling past the end of the program and produced a target
  // that pointed backwards.
  const rawExpectedTotalDays = daysSinceStart + 1; // +1 because day 1 is start date
  const expectedTotalDays = maxWeeks && maxWeeks > 0
    ? Math.min(rawExpectedTotalDays, maxWeeks * 7)
    : rawExpectedTotalDays;
  const expectedWeek = Math.floor((expectedTotalDays - 1) / 7) + 1;
  const expectedDay = ((expectedTotalDays - 1) % 7) + 1;

  // Where user actually is
  const actualTotalDays = (currentWeek - 1) * 7 + currentDay;
  const daysBehind = Math.max(0, expectedTotalDays - actualTotalDays);

  // Determine status and message
  let message = '';
  let isOnTrack = daysBehind <= 2; // Allow 2 days grace

  if (daysBehind === 0) {
    message = "You're on track! ";
  } else if (daysBehind <= 2) {
    message = `${daysBehind} day${daysBehind > 1 ? 's' : ''} behind - no worries, keep going!`;
  } else if (daysBehind <= 7) {
    message = `${daysBehind} days behind schedule. Complete today's workout to catch up!`;
  } else {
    message = `${daysBehind} days behind. Consider jumping ahead or picking up where you left off.`;
  }

  return {
    daysBehind,
    expectedWeek,
    expectedDay,
    isOnTrack,
    message,
  };
}

/**
 * Get the user's current program state.
 * Auto-advances to stay in sync with the calendar — missed days are
 * marked "skipped" so the user always sees the right day for today's date.
 */
export async function getUserProgramState(userId: string): Promise<{
  selection: UserProgramSelection;
  scheduleStatus: ReturnType<typeof calculateScheduleStatus>;
  currentWeek: number;
  currentDay: number;
} | null> {
  try {
    const selection = await getUserProgramSelection(userId);
    if (!selection) return null;

    // Step 9: Guard against regression — if the loaded position is behind
    // what Dexie has, use the further-ahead position.
    // EXCEPTION: If the program was restarted (restartedAt timestamp), skip the guard
    // so the restart actually takes effect across all devices.
    try {
      const dexieProfile = await dexieDb.userProfiles.get(userId);
      if (dexieProfile?.programSelection && dexieProfile.programSelection.programId === selection.programId) {
        // Check if a restart happened — if either side has a newer restartedAt
        // than the other's updatedAt, the restart wins (don't regress to old position)
        const loadedRestartedAt = (selection as any).restartedAt ? new Date((selection as any).restartedAt).getTime() : 0;
        const dexieRestartedAt = (dexieProfile.programSelection as any).restartedAt ? new Date((dexieProfile.programSelection as any).restartedAt).getTime() : 0;
        const dexieUpdatedAt = dexieProfile.programSelection.updatedAt ? new Date(dexieProfile.programSelection.updatedAt).getTime() : 0;
        const newestRestart = Math.max(loadedRestartedAt, dexieRestartedAt);
        const wasRestarted = newestRestart > 0;

        if (wasRestarted) {
          // Use whichever side has the restart data
          const restartIsLoaded = loadedRestartedAt >= dexieRestartedAt;
          const restartWeek = restartIsLoaded ? selection.currentWeek : dexieProfile.programSelection.currentWeek;
          const restartDay = restartIsLoaded ? selection.currentDay : dexieProfile.programSelection.currentDay;
          const restartCompleted = restartIsLoaded ? (selection.completedDays || []) : (dexieProfile.programSelection.completedDays || []);
          const restartSkipped = restartIsLoaded ? (selection.skippedDays || []) : (dexieProfile.programSelection.skippedDays || []);
          const restartStartDate = restartIsLoaded
            ? (selection.startDate instanceof Date ? selection.startDate.toISOString() : String(selection.startDate))
            : dexieProfile.programSelection.startDate;

          console.log(`[dayTracking] Restart detected (${new Date(newestRestart).toISOString()}) — skipping regression guard, accepting W${restartWeek}D${restartDay}`);

          selection.currentWeek = restartWeek;
          selection.currentDay = restartDay;
          selection.completedDays = restartCompleted;
          selection.skippedDays = restartSkipped;

          // Update Dexie to match the restart so future loads don't regress
          await dexieDb.userProfiles.update(userId, {
            programSelection: {
              ...dexieProfile.programSelection,
              currentWeek: restartWeek,
              currentDay: restartDay,
              completedDays: restartCompleted,
              skippedDays: restartSkipped,
              startDate: restartStartDate,
              updatedAt: new Date().toISOString(),
              restartedAt: new Date(newestRestart).toISOString(),
            },
          });
        } else {
          const loadedScalar = (selection.currentWeek - 1) * 7 + selection.currentDay;
          const dexieScalar = (dexieProfile.programSelection.currentWeek - 1) * 7 + dexieProfile.programSelection.currentDay;
          if (dexieScalar > loadedScalar) {
            console.log(`[dayTracking] Regression guard: loaded W${selection.currentWeek}D${selection.currentDay} behind Dexie W${dexieProfile.programSelection.currentWeek}D${dexieProfile.programSelection.currentDay}, using Dexie position`);
            selection.currentWeek = dexieProfile.programSelection.currentWeek;
            selection.currentDay = dexieProfile.programSelection.currentDay;
          }
        }
      }
    } catch {
      // Non-critical: if Dexie read fails, proceed with loaded values
    }

    const startDate = selection.startDate instanceof Date
      ? selection.startDate
      : new Date(selection.startDate);

    // The program's REAL length, resolved once and reused by everything below
    // (schedule status, the auto-advance bound, the second status call).
    // Previously each of these assumed 13 weeks independently.
    const programWeeks = await resolveProgramDurationWeeks(selection.programId, userId);

    // Calculate schedule status
    const scheduleStatus = calculateScheduleStatus(
      startDate,
      selection.currentWeek,
      selection.currentDay,
      selection.completedDays || [],
      programWeeks
    );

    // Skip auto-advance if the user manually navigated recently.
    // This prevents the calendar-based advance from immediately overriding
    // a Day Navigator jump (e.g., going back to redo a day or skipping ahead).
    // Auto-advance resumes after the user completes a session (which clears
    // the manual navigation flag via markDayCompleted).
    // `positionSetAt` — the same stamp the sync merge uses to let an explicit
    // choice beat its never-regress rule. It replaced `manualNavigationAt`,
    // which was set here but dropped by every storage whitelist, so this guard
    // read undefined on every load and NEVER fired: the calendar reclaimed the
    // pointer the moment the user navigated anywhere.
    const manualNavAt = (selection as any).positionSetAt;
    const manualNavRecent = manualNavAt && (Date.now() - new Date(manualNavAt).getTime()) < 24 * 60 * 60 * 1000; // within 24hrs
    if (manualNavRecent) {
      console.log('Skipping auto-advance: user manually navigated at', manualNavAt);
    }

    // Don't auto-advance for users who have never logged a session in this
    // program. They picked the program but haven't started training; the
    // calendar shouldn't tick the program forward from under them. This
    // matters most for free-preview users — they have ONE workout to
    // experience the product, and if the system auto-advances past W1D1
    // their preview is consumed by an empty calendar tick.
    const hasEverTrained = (selection.completedDays || []).length > 0;
    if (!hasEverTrained && scheduleStatus.daysBehind > 0) {
      console.log('Skipping auto-advance: user has not completed any sessions yet — staying on W1D1');
    }

    /**
     * Don't auto-advance during a training break. THIS is what "pause my
     * program" means, and it was missing entirely.
     *
     * `startBreak()` only snapshots week/day — it never stopped this block
     * running. So while a user was on a sanctioned travel break, every
     * dashboard load walked their pointer forward to the calendar date and
     * recorded each training day it passed as SKIPPED. A 10-day trip left the
     * program 10 days ahead with ~7 fabricated skips, and because
     * `mergeProgramSelection` unions `skippedDays`, those skips came back from
     * Firestore forever. The user's words: "it didn't pause my program".
     *
     * Failing open (advancing) on a read error is deliberate: a Dexie hiccup
     * should not silently freeze a program for someone with no active break.
     */
    let onBreak = false;
    try {
      const { getActiveBreak } = await import('./trainingBreak');
      onBreak = !!(await getActiveBreak(userId));
    } catch {
      // Non-critical: treat as "no break" and behave as before.
    }
    if (onBreak && scheduleStatus.daysBehind > 0) {
      console.log('Skipping auto-advance: user is on an active training break');
    }

    // Auto-advance: if user is behind by 1+ days, skip forward so
    // the displayed day matches the calendar. Missed days are recorded
    // as "skipped" so progress tracking stays accurate.
    if (scheduleStatus.daysBehind > 0 && !manualNavRecent && hasEverTrained && !onBreak) {
      const { expectedWeek, expectedDay } = scheduleStatus;

      // Real program length, already resolved above. Keep the program object
      // around too so we can classify each gap day — rest days must NOT be
      // recorded as "skipped."
      let maxWeek = programWeeks;
      let program: any = null;
      let getWorkoutTemplate: ((p: any, w: number, d: number) => any) | null = null;
      let classifyDayType: ((t: any) => string) | null = null;
      try {
        const { getCachedProgram } = await import('./programCache');
        program = await getCachedProgram(selection.programId, userId);
        if (program?.metadata?.duration) maxWeek = program.metadata.duration;
        ({ getWorkoutTemplate } = await import('./workoutGenerator'));
        ({ classifyDayType } = await import('./workoutEnrichment'));
      } catch { /* default 13; classification optional */ }

      // Returns true when (w,d) is a rest day (active or passive) for
      // this program. Rest days are part of the plan, not a miss — so
      // they should never be auto-recorded as skipped.
      const isRestDay = (w: number, d: number): boolean => {
        if (!program || !getWorkoutTemplate || !classifyDayType) return false;
        try {
          const tmpl = getWorkoutTemplate(program, w, d);
          return classifyDayType(tmpl) !== 'training';
        } catch {
          return false;
        }
      };

      // Only advance if we wouldn't exceed the program
      if (expectedWeek <= maxWeek) {
        // Collect days to skip (between current position and expected)
        const existingSkipped = new Set(
          (selection.skippedDays || []).map(d => `${d.week}-${d.day}`)
        );
        const existingCompleted = new Set(
          (selection.completedDays || []).map(d => `${d.week}-${d.day}`)
        );

        let w = selection.currentWeek;
        let d = selection.currentDay;
        while (w < expectedWeek || (w === expectedWeek && d < expectedDay)) {
          const key = `${w}-${d}`;
          // Only training days that were genuinely missed count as skipped.
          // Rest days (active or passive) are part of the plan — gliding
          // past them is expected, not a failure.
          if (!existingCompleted.has(key) && !existingSkipped.has(key) && !isRestDay(w, d)) {
            selection.skippedDays.push({ week: w, day: d });
          }
          d++;
          if (d > 7) { d = 1; w++; }
          if (w > maxWeek) break;
        }

        selection.currentWeek = expectedWeek;
        selection.currentDay = expectedDay;

        // Persist the advance — await so localStorage/Dexie are updated before
        // AppContext reads back stale week/day values.
        try {
          await saveUserProgramSelection(userId, selection);
        } catch (err) {
          console.error('Failed to persist day auto-advance:', err);
        }

        console.log(`Auto-advanced to Week ${expectedWeek} Day ${expectedDay} (was ${scheduleStatus.daysBehind} days behind)`);
      }
    }

    return {
      selection,
      scheduleStatus: calculateScheduleStatus(
        startDate,
        selection.currentWeek,
        selection.currentDay,
        selection.completedDays || [],
        programWeeks
      ),
      currentWeek: selection.currentWeek,
      currentDay: selection.currentDay,
    };
  } catch (error) {
    console.error('Error getting user program state:', error);
    return null;
  }
}

/**
 * DEPRECATED: Old auto-skip function - kept for reference only
 * DO NOT USE - this goes against industry best practices
 */
export function calculateAutoSkippedDays(
  _startDate: Date,
  _currentWeek: number,
  _currentDay: number,
  _daysPerWeek: number = 7
): { week: number; day: number }[] {
  console.warn(' calculateAutoSkippedDays is deprecated - workouts should persist until completed');
  return []; // Return empty - no auto-skip
}

/**
 * Manually set the current day (for navigation)
 */
export async function setCurrentDay(
  userId: string,
  week: number,
  day: number
): Promise<void> {
  try {
    const selection = await getUserProgramSelection(userId);
    if (!selection) {
      throw new Error('No program selected');
    }
    
    // Validate week/day against the program's REAL length. This was hardcoded
    // to 13, so every user of the 37-week prenatal program or 14-week HST was
    // refused past week 13 — and because every caller swallows the throw into
    // a console.error, the tap simply did nothing with no explanation.
    const maxWeeks = await resolveProgramDurationWeeks(selection.programId, userId);
    if (week < 1 || week > maxWeeks) {
      throw new Error(`Week must be between 1 and ${maxWeeks}`);
    }
    if (day < 1 || day > 7) {
      throw new Error('Day must be between 1 and 7');
    }
    
    console.log(` Manually setting day to Week ${week} Day ${day}`);

    // Auto-unskip: if the target day was skipped, remove it from skippedDays
    const skipKey = `${week}-${day}`;
    const wasSkipped = selection.skippedDays?.some(
      d => `${d.week}-${d.day}` === skipKey
    );
    if (wasSkipped) {
      selection.skippedDays = selection.skippedDays.filter(
        d => `${d.week}-${d.day}` !== skipKey
      );
      // Tombstone it — see unskipDay. A bare removal does not survive the
      // union merge, so without this the day reappears as a "missed drill"
      // moments after the user navigated to it.
      (selection as any).unskippedDays = [
        ...((selection as any).unskippedDays || []).filter(
          (d: { week: number; day: number }) => `${d.week}-${d.day}` !== skipKey
        ),
        { week, day },
      ];
      console.log(` Auto-unskipped Week ${week} Day ${day}`);
    }

    selection.currentWeek = week;
    selection.currentDay = day;

    // Record that the USER chose this position. Two things depend on it:
    // calendar auto-advance stands down for 24h (getUserProgramState), and the
    // sync merge lets this position beat its max-scalar never-regress rule —
    // without which a backward move is reverted inside this very save.
    (selection as any).positionSetAt = new Date().toISOString();
    // Record WHICH position the stamp refers to. Without this the merge could
    // only honour the intent while the stamp was strictly newer than the other
    // side's — true exactly once, until the write synced — after which
    // max-scalar never-regress reverted every backward move. See syncMerge.
    (selection as any).positionWeek = week;
    (selection as any).positionDay = day;

    await saveUserProgramSelection(userId, selection);
  } catch (error) {
    console.error('Error setting current day:', error);
    throw error;
  }
}

/**
 * Complete a makeup (skipped) day without changing program position.
 * Removes the day from skippedDays and adds it to completedDays.
 */
export async function completeMakeupDay(
  userId: string,
  week: number,
  day: number
): Promise<void> {
  try {
    const selection = await getUserProgramSelection(userId);
    if (!selection) throw new Error('No program selected');

    // Remove from skippedDays
    selection.skippedDays = (selection.skippedDays || []).filter(
      d => !(d.week === week && d.day === day)
    );

    // Add to completedDays if not already there
    const alreadyCompleted = (selection.completedDays || []).some(
      d => d.week === week && d.day === day
    );
    if (!alreadyCompleted) {
      selection.completedDays = [...(selection.completedDays || []), { week, day }];
    }

    // Save WITHOUT changing currentWeek/currentDay
    await saveUserProgramSelection(userId, selection);
    console.log(`[dayTracking] Makeup completed: W${week}D${day} — position unchanged at W${selection.currentWeek}D${selection.currentDay}`);
  } catch (error) {
    console.error('Error completing makeup day:', error);
    throw error;
  }
}

/**
 * Jump to a future day and automatically skip all days in between
 * Use case: User completed workouts off-schedule and wants to start fresh on a future day
 */
export async function jumpToFutureDay(
  userId: string,
  targetWeek: number,
  targetDay: number
): Promise<{ daysSkipped: number; skippedDays: { week: number; day: number }[] }> {
  try {
    const selection = await getUserProgramSelection(userId);
    if (!selection) {
      throw new Error('No program selected');
    }
    
    // Validate target week/day against the program's REAL length — see the
    // matching note in setCurrentDay.
    const maxWeeks = await resolveProgramDurationWeeks(selection.programId, userId);
    if (targetWeek < 1 || targetWeek > maxWeeks) {
      throw new Error(`Week must be between 1 and ${maxWeeks}`);
    }
    if (targetDay < 1 || targetDay > 7) {
      throw new Error('Day must be between 1 and 7');
    }
    
    const currentWeek = selection.currentWeek;
    const currentDay = selection.currentDay;
    
    // Calculate total days
    const currentTotalDays = (currentWeek - 1) * 7 + currentDay;
    const targetTotalDays = (targetWeek - 1) * 7 + targetDay;
    
    // Can't jump backward
    if (targetTotalDays <= currentTotalDays) {
      throw new Error('Target day must be in the future');
    }
    
    // Calculate all days to skip
    const daysToSkip: { week: number; day: number }[] = [];
    let skipWeek = currentWeek;
    let skipDay = currentDay;
    
    while (skipWeek < targetWeek || (skipWeek === targetWeek && skipDay < targetDay)) {
      // Don't skip if already completed
      const isCompleted = selection.completedDays.some(
        d => d.week === skipWeek && d.day === skipDay
      );
      
      if (!isCompleted) {
        daysToSkip.push({ week: skipWeek, day: skipDay });
      }
      
      // Advance to next day
      skipDay++;
      if (skipDay > 7) {
        skipDay = 1;
        skipWeek++;
      }
      
      // Safety limit — the program's own length, not a hardcoded 13.
      if (skipWeek > maxWeeks) break;
    }
    
    console.log(`⏭ Jumping to Week ${targetWeek} Day ${targetDay}, skipping ${daysToSkip.length} days`);
    
    // Add to skipped days (avoid duplicates)
    const existingSkipped = new Set(
      selection.skippedDays.map(d => `${d.week}-${d.day}`)
    );
    
    daysToSkip.forEach(day => {
      const key = `${day.week}-${day.day}`;
      if (!existingSkipped.has(key)) {
        selection.skippedDays.push(day);
      }
    });
    
    // Set new current day
    selection.currentWeek = targetWeek;
    selection.currentDay = targetDay;

    // Realign startDate so the calendar maps to the new position.
    // Without this, the user is now AHEAD of the calendar (their jumped
    // position > expected calendar day), and calculateScheduleStatus's
    // auto-advance never fires until calendar drift catches up — could
    // be a week or more. Setting startDate so today === the new
    // currentDay makes tomorrow naturally become currentDay + 1 via the
    // existing auto-advance, which is what users expect post-jump.
    // (`targetTotalDays` is already in scope from the validation above.)
    //
    // startDate doubles as the SCHEDULE ANCHOR (what the calendar measures
    // from) and as the record of when the user actually began. Realigning
    // overwrote both, unconditionally, on an ordinary advertised forward jump —
    // and nothing else stores the real start, so that value was gone for good.
    // Capture it once, the first time it is about to be moved, and leave the
    // anchor semantics of startDate untouched so no schedule math changes.
    if (!selection.originalStartDate) {
      const priorStart = selection.startDate instanceof Date
        ? selection.startDate
        : new Date(selection.startDate);
      if (!isNaN(priorStart.getTime())) {
        selection.originalStartDate = priorStart.toISOString();
      }
    }

    const realignedStart = new Date();
    realignedStart.setDate(realignedStart.getDate() - (targetTotalDays - 1));
    selection.startDate = realignedStart;

    // Save to Firestore
    await saveUserProgramSelection(userId, selection);
    
    //  No localStorage cache to clear - using Dexie now
    
    return {
      daysSkipped: daysToSkip.length,
      skippedDays: daysToSkip
    };
  } catch (error) {
    console.error('Error jumping to future day:', error);
    throw error;
  }
}

/**
 * Mark a day as completed and advance to next day
 * @param programDuration - Max weeks in the program (e.g. 6, 12, 13). Defaults to 13 if not provided.
 * @returns nextWeek, nextDay, and programCompleted flag
 */
export async function markDayCompleted(
  userId: string,
  week: number,
  day: number,
  programDuration?: number
): Promise<{ nextWeek: number; nextDay: number; programCompleted: boolean }> {
  try {
    const selection = await getUserProgramSelection(userId);
    if (!selection) {
      throw new Error('No program selected');
    }

    // Resolve the real length when the caller didn't supply one. Defaulting to
    // 13 meant callers that omit programDuration (the rest-day path, among
    // others) pushed 12-week users into a nonexistent week 13 — no completion
    // event, no archetype unlock, stranded on a program that never ends.
    const maxWeek = programDuration || await resolveProgramDurationWeeks(selection.programId, userId);

    // Add to completed days
    const completedKey = `${week}-${day}`;
    const alreadyCompleted = selection.completedDays.some(
      d => `${d.week}-${d.day}` === completedKey
    );

    if (!alreadyCompleted) {
      selection.completedDays.push({ week, day });
      console.log(` Marked Week ${week} Day ${day} as completed`);
    }

    // Remove from skipped if it was there
    selection.skippedDays = selection.skippedDays.filter(
      d => `${d.week}-${d.day}` !== completedKey
    );

    // Calculate next day
    let nextWeek = week;
    let nextDay = day + 1;

    if (nextDay > 7) {
      nextDay = 1;
      nextWeek++;
    }

    // Advance to the next genuinely-incomplete day: skip over both manually
    // SKIPPED days and days ALREADY COMPLETED out of order. The completed-skip
    // is what makes the "Do it today" / makeup flows clean — if you did Friday
    // early (via completeMakeupDay, position unchanged), then finish today's
    // workout, this hops the pointer over Friday instead of stranding you on a
    // day that's already checked off.
    let safetyCounter = 0;
    while (safetyCounter < 100 && nextWeek <= maxWeek) {
      const isSkipped = selection.skippedDays.some(
        d => d.week === nextWeek && d.day === nextDay
      );
      const isDone = selection.completedDays.some(
        d => d.week === nextWeek && d.day === nextDay
      );

      if (!isSkipped && !isDone) break;

      console.log(`⏭ Skipping Week ${nextWeek} Day ${nextDay} (${isSkipped ? 'skipped' : 'already completed'})`);
      nextDay++;
      if (nextDay > 7) {
        nextDay = 1;
        nextWeek++;
      }
      safetyCounter++;
    }

    // Detect program completion: nextWeek exceeds program duration
    let programCompleted = false;
    if (nextWeek > maxWeek) {
      programCompleted = true;
      // Keep nextWeek at maxWeek+1 so smartWorkoutGenerator also detects completion
      nextWeek = maxWeek + 1;
      nextDay = 1;
      console.log(` Program complete! Week ${week} Day ${day} was the final workout.`);
    }

    selection.currentWeek = nextWeek;
    selection.currentDay = nextDay;

    // Clear the explicit-position stamp — the session is done, so calendar
    // auto-advance should resume from the new position and the merge should go
    // back to its never-regress default.
    delete (selection as any).positionSetAt;

    await saveUserProgramSelection(userId, selection);

    //  No localStorage cache to clear - using Dexie now

    console.log(` Advanced to Week ${nextWeek} Day ${nextDay}`);

    return { nextWeek, nextDay, programCompleted };
  } catch (error) {
    console.error('Error marking day completed:', error);
    throw error;
  }
}

/**
 * Reset program progress to Week 2 Day 1 (skips Week 1 assessment)
 * Used when user wants to repeat a completed program.
 * Skips Week 1 because it's an assessment week and the user just finished one.
 * Does not affect streak.
 */
export async function resetProgramProgress(userId: string): Promise<void> {
  try {
    const selection = await getUserProgramSelection(userId);
    if (!selection) {
      throw new Error('No program selected');
    }

    selection.currentWeek = 2;
    selection.currentDay = 1;
    selection.completedDays = [];
    selection.skippedDays = [];
    selection.startDate = new Date();

    await saveUserProgramSelection(userId, selection);
    console.log(' Program progress reset to Week 2 Day 1 (skipping assessment week)');
  } catch (error) {
    console.error('Error resetting program progress:', error);
    throw error;
  }
}

/**
 * Manually skip a day
 */
export async function skipDay(
  userId: string,
  week: number,
  day: number
): Promise<void> {
  try {
    const selection = await getUserProgramSelection(userId);
    if (!selection) {
      throw new Error('No program selected');
    }
    
    const key = `${week}-${day}`;
    const alreadySkipped = selection.skippedDays.some(
      d => `${d.week}-${d.day}` === key
    );
    
    if (!alreadySkipped) {
      selection.skippedDays.push({ week, day });
      console.log(`⏭ Manually skipped Week ${week} Day ${day}`);
    }

    // Retire any un-skip tombstone for this day — otherwise the tombstone
    // would keep suppressing the skip the user just asked for.
    (selection as any).unskippedDays = ((selection as any).unskippedDays || []).filter(
      (d: { week: number; day: number }) => `${d.week}-${d.day}` !== key
    );


    // Remove from completed if it was there
    selection.completedDays = selection.completedDays.filter(
      d => `${d.week}-${d.day}` !== key
    );
    
    // If we're skipping the current day, advance to next
    if (selection.currentWeek === week && selection.currentDay === day) {
      let nextWeek = week;
      let nextDay = day + 1;
      
      if (nextDay > 7) {
        nextDay = 1;
        nextWeek++;
      }
      
      selection.currentWeek = nextWeek;
      selection.currentDay = nextDay;
    }
    
    await saveUserProgramSelection(userId, selection);
    
    //  No localStorage cache to clear - using Dexie now
  } catch (error) {
    console.error('Error skipping day:', error);
    throw error;
  }
}

/**
 * Unskip a day (allow user to go back and complete it)
 */
export async function unskipDay(
  userId: string,
  week: number,
  day: number
): Promise<void> {
  try {
    const selection = await getUserProgramSelection(userId);
    if (!selection) {
      throw new Error('No program selected');
    }
    
    const key = `${week}-${day}`;
    const wasSkipped = selection.skippedDays.some(
      d => `${d.week}-${d.day}` === key
    );
    
    if (wasSkipped) {
      selection.skippedDays = selection.skippedDays.filter(
        d => `${d.week}-${d.day}` !== key
      );
      // Tombstone the removal. skippedDays merges as a UNION, so deleting an
      // entry locally is undone by the next sync — the other side still has it
      // and the union puts it straight back. Without this the un-skip visibly
      // works and then silently reverts on the next sync tick.
      selection.unskippedDays = [
        ...((selection as any).unskippedDays || []).filter(
          (d: { week: number; day: number }) => `${d.week}-${d.day}` !== key
        ),
        { week, day },
      ];
      console.log(`↩ Unskipped Week ${week} Day ${day}`);

      await saveUserProgramSelection(userId, selection);
      
      //  No localStorage cache to clear - using Dexie now
    }
  } catch (error) {
    console.error('Error unskipping day:', error);
    throw error;
  }
}

/**
 * Check if a day is completed
 */
export function isDayCompleted(
  selection: UserProgramSelection,
  week: number,
  day: number
): boolean {
  return selection.completedDays.some(
    d => d.week === week && d.day === day
  );
}

/**
 * Check if a day is skipped
 */
export function isDaySkipped(
  selection: UserProgramSelection,
  week: number,
  day: number
): boolean {
  return selection.skippedDays.some(
    d => d.week === week && d.day === day
  );
}

/**
 * Get day status
 */
export type DayStatus = 'completed' | 'skipped' | 'current' | 'available' | 'future';

export function getDayStatus(
  selection: UserProgramSelection,
  week: number,
  day: number
): DayStatus {
  if (isDayCompleted(selection, week, day)) {
    return 'completed';
  }
  
  if (isDaySkipped(selection, week, day)) {
    return 'skipped';
  }
  
  if (selection.currentWeek === week && selection.currentDay === day) {
    return 'current';
  }
  
  // Calculate if this day is in the past (available to do) or future
  const totalDays = (week - 1) * 7 + day;
  const currentTotalDays = (selection.currentWeek - 1) * 7 + selection.currentDay;
  
  if (totalDays < currentTotalDays) {
    return 'available'; // Past day, can go back and do it
  }
  
  return 'future'; // Future day
}

